/* Three platform numbers under the hero. Creators and video seconds are the
   exact live totals from landingStats (cached an hour server-side); only the
   countries figure is a hand-refreshed PostHog geo estimate, so it alone keeps
   the "+". Counts up once the band scrolls into view AND the query has
   settled, so the viewer never sees a stale figure jump to the real one. */
import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useTranslation } from "react-i18next";

// Countries with 10+ signed-up accounts (PostHog geo on identified persons,
// 2026-09-02: ~150 of 211 codes seen). Rounded down so the "+" holds.
const COUNTRIES = 150;
// Used only if the query fails (prod totals on 2026-09-20).
const FALLBACK = { creators: 81281, videoSeconds: 1352983 };

function useCountUp(target: number, active: boolean, ms = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (matchMedia("(prefers-reduced-motion:reduce)").matches) {
      setValue(target);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, ms]);
  return value;
}

export default function StatsBand() {
  const { t } = useTranslation();
  const stats = trpc.landingStats.get.useQuery(undefined, {
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const creators = stats.data?.creators ?? FALLBACK.creators;
  const seconds = stats.data?.videoSeconds ?? FALLBACK.videoSeconds;

  const ref = useRef<HTMLElement | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const settled = !stats.isLoading;
  const c = useCountUp(creators, seen && settled);
  const s = useCountUp(seconds, seen && settled);
  const k = useCountUp(COUNTRIES, seen);
  const fmt = (n: number) => n.toLocaleString("en-US");

  return (
    <section className="stats" ref={ref} aria-label={t("landingV2.stats.aria")}>
      <div className="stats-in">
        <div className="stat">
          <span className="n">{fmt(c)}</span>
          <span className="l">{t("landingV2.stats.creators")}</span>
        </div>
        <div className="stat">
          <span className="n">{fmt(s)}</span>
          <span className="l">{t("landingV2.stats.seconds")}</span>
        </div>
        <div className="stat">
          <span className="n">{fmt(k)}+</span>
          <span className="l">{t("landingV2.stats.countries")}</span>
        </div>
      </div>
    </section>
  );
}
